import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import withDerivedProps from "lib/withDerivedProps";

// Components
import I18nHOC from "UIComponents/I18nHOC";
import UIModal from "UIComponents/UIModal";
import Accordian from "UIComponents/Accordian";
import CriteriaRating from "UIComponents/CriteriaRating";
import Loading from "UIComponents/Loading";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import withLoader from "UIComponents/GqlWithLoader";
import SkeletonLoader from "UIComponents/SkeletonLoader";

// Graphql
import {
  getCourseSubjectFlatVariantsLevelQuery,
  getCategoryScoreValuesQuery,
  getLearningCourseSubjectFlatVariantsLevelQuery,
} from "ClassRoom/modules/ClassRoomQuery";
import {
  getSubjectFlatVariantsLevelFromCache,
  getCategoryScoreValuesFromCache,
} from "ClassRoom/modules/ClassRoomGraphqlHelpers";
import { getOrganizationPointSetQuery } from "ScoreCategories/modules/ScoreCategoriesQueries";
import { getOrganizationPointSetFromCache } from "ScoreCategories/modules/ScoreCategoriesGraphqlHelpers";

// Modules
import { createAssessmentTool } from "AssessmentToolSetup/modules/AssessmentToolSetupModule";
import {
  updateCategoryGradeBoundary,
  getScoreDetailsMemoize,
} from "ScoreCategories/modules/ScoreCategoriesModule";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { removeAssessmentTool } from "Assessments/modules/AssessmentModule";
import { updateQueryParameters } from "modules/NavigationModule";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";

// Design system
import {
  Checkbox,
  Table,
  TextInput,
  Tooltip,
  Button,
  IconButton,
  Dropdown,
  DropdownMenu,
  ButtonDisclosure,
} from "@toddle-edu/ds-web";
import {
  CloseOutlined,
  ChevronRightOutlined,
  InformationOutlined,
} from "@toddle-edu/ds-icons";

// third party libraries
import { compose, graphql } from "lib/apolloClient";
import classNames from "classnames";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { Popover, ArrowContainer } from "@toddle-edu/react-tiny-popover";

// utils
import { getSubjectNameAndGenericTags } from "GradeBoundaries/modules/Utils";
import {
  getCategoryGradeBoundaryValues,
  checkAreScoresValid,
  copySLGradeScaleToHL,
  checkIsSameScale,
} from "ScoreCategories/modules/Utils";
import {
  getCategoriesForGradeBoundaryTool,
  getIsIBGradeScaleTool,
  getAutoEvaluateIBGradeToolTypeName,
  getGradeBoundaryMaxVal,
  getStudentTags,
} from "ClassRoom/modules/utils";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { getSelectedLearningCourseId } from "LearningCourse/modules/Utils";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";
import { getResolvedMinimalTreeSet } from "../../../Assessments/modules/Utils";

// Constants
import {
  ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
} from "Constants/stringConstants";
import { colors, DOUBLE_DIGIT_DECIMAL_NUMBERS_REGEX } from "Constants";

// Scss
import classes from "./AutomaticIBScaleTool.scss";

const styles = {
  textInputStyles: {
    width: "76px",
  },
  criteriaRatingContainerStyle: {
    width: "max-content",
  },
  leftArrowStyle: {
    alignItems: "flex-start",
    width: "24px",
    height: "24px",
  },
  headerStyle: {
    padding: 0,
  },
  itemTitleContainerStyle: {
    maxWidth: "352px",
  },
  popoverContainerStyle: {
    zIndex: 2500,
  },
};

const IB_GRADE_SCALE = [1, 2, 3, 4, 5, 6, 7];
const IB_TOK_GRADE_SCALE = ["E", "D", "C", "B", "A"];

const getGradeBoundaryInitialValues = ({ subjectType }) => {
  const isTOKSubject = _.isEqual(subjectType, "TOK");
  const gradeScale = isTOKSubject ? IB_TOK_GRADE_SCALE : IB_GRADE_SCALE;
  return gradeScale.map(grade => ({
    id: grade,
    grade,
    SL: {
      minValue: _.isEqual(grade, isTOKSubject ? "E" : 1) ? "0" : "",
      maxValue: "",
    },
    HL: {
      minValue: _.isEqual(grade, isTOKSubject ? "E" : 1) ? "0" : "",
      maxValue: "",
    },
  }));
};

const getShouldCreateNewTool = params => {
  const { gradeBoundaryMethod, assessmentToolType } = params;

  if (_.isEmpty(assessmentToolType)) return false;

  let prevSelectedMethod = "";

  switch (assessmentToolType) {
    case ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY:
      prevSelectedMethod = "category_boundary";
      break;
    case ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY:
      prevSelectedMethod = "term_boundary";
      break;
    default:
      prevSelectedMethod = "custom_boundary";
  }

  return !_.isEqual(prevSelectedMethod, gradeBoundaryMethod);
};

const getGradeBoundaryOptions = ({
  t,
  hasCategoryGradeBoundary,
  hasTermGradeBoundary,
  categoryId,
  assessmentToolType,
}) => {
  return [
    {
      key: "category_boundary",
      label: t("classRoom:use_category_grade_boundary"),
      subText: t("classRoom:use_category_grade_boundary_subtext"),
      disabled:
        !hasCategoryGradeBoundary &&
        assessmentToolType !== ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
      disabledTooltip: categoryId
        ? t("classRoom:no_category_linked_with_assessment")
        : t("classRoom:assign_category_to_task"),
    },
    {
      key: "term_boundary",
      label: t("classRoom:use_term_grade_boundary"),
      subText: t("classRoom:use_term_grade_boundary_subtext"),
      disabled:
        !hasTermGradeBoundary &&
        assessmentToolType !== ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
      disabledTooltip: t("classRoom:no_term_configured_for_subject"),
    },
    {
      key: "custom_boundary",
      label: t("classRoom:define_custom_grade_boundary"),
      subText: t("classRoom:define_custom_grade_boundary_subtext"),
    },
  ];
};

const getFilteredGradeBoundaries = ({
  gradeBoundary,
  subjectLevelGenericTags,
  scoreKey = "values",
}) => {
  let tags = [];
  if (!_.isEmpty(subjectLevelGenericTags)) {
    tags = _.uniq(_.map(subjectLevelGenericTags, tag => _.get(tag, "id")));
  } else {
    tags = [null];
  }

  const categories = _.get(gradeBoundary, "categories", []);

  return {
    ...gradeBoundary,
    categories: _.map(categories, category => {
      const values = _.get(category, `${scoreKey}`, []);
      return {
        ...category,
        [`${scoreKey}`]: _.filter(values, value => {
          const genericTagId = _.get(value, "genericTag.id", null);
          return _.includes(tags, genericTagId);
        }),
      };
    }),
  };
};

const getTableRowValue = params => {
  const {
    t,
    header,
    boundary,
    onChangeInput,
    isFirstRow,
    isLastRow,
    mode,
    isEditable,
    errors = {},
    autoFocusKey,
    setAutoFocusKey,
    index,
    disableTextInput,
    errorsCount,
    showModalInViewModal,
  } = params;
  const { key } = header || {};

  switch (key) {
    case "GRADE_SCALE": {
      return _.get(boundary, "grade");
    }

    case "SCORE_RANGE_SL":
    case "SCORE_RANGE_HL":
    case "SAME_SCALE":
    case "SCORE_RANGE": {
      const { HL, SL, id, grade } = boundary || {};
      const { minValue: minValueSL, maxValue: maxValueSL } = SL || {};
      const { minValue: minValueHL, maxValue: maxValueHL } = HL || {};

      const isHL = key === "SCORE_RANGE_HL";

      const tagLabel = isHL ? "HL" : "SL";

      const minScoreKey = `${grade}_${tagLabel}_minScore`;
      const maxScoreKey = `${grade}_${tagLabel}_maxScore`;

      const { errorMsg: minScoreErrorMsg = "" } = _.get(
        errors,
        minScoreKey,
        {}
      );
      const { errorMsg: maxScoreErrorMsg = "" } = _.get(
        errors,
        maxScoreKey,
        {}
      );

      const minScoreHasError =
        errorsCount !== 0 && !_.isEmpty(minScoreErrorMsg);
      const maxScoreHasError =
        errorsCount !== 0 && !_.isEmpty(maxScoreErrorMsg);

      const textInputContainerClass = classNames({
        [classes.textInputContainer]: true,
        [classes.textInputLastRow]: isLastRow,
        [classes.textInputLastRowViewMode]: isLastRow && showModalInViewModal,
        [classes.textInputViewMode]: mode === "view",
      });

      return (
        <div className={textInputContainerClass}>
          {isEditable ? (
            <ConditionalWrapper
              condition={minScoreHasError}
              wrapper={
                <Tooltip
                  placement={"left"}
                  tooltip={minScoreErrorMsg}
                  showArrow={true}
                />
              }
              type="wrapperAsElement"
            >
              <TextInput
                size={"large"}
                value={isHL ? minValueHL : minValueSL}
                style={styles.textInputStyles}
                onChange={e => {
                  onChangeInput({
                    id,
                    isMinValue: true,
                    value: e.target.value,
                    isHL,
                    index,
                  });
                }}
                disabled={isFirstRow || disableTextInput}
                error={minScoreHasError}
                autoFocus={autoFocusKey === minScoreKey}
                onFocus={() => setAutoFocusKey(minScoreKey)}
                onBlur={() => setAutoFocusKey("")}
              />
            </ConditionalWrapper>
          ) : (
            <div className={classes.score}>
              {isHL ? minValueHL : minValueSL}
            </div>
          )}

          {isLastRow ? <div className={classes.hyphen}>{"-"}</div> : null}

          {isLastRow ? (
            isEditable ? (
              <ConditionalWrapper
                condition={maxScoreHasError}
                wrapper={
                  <Tooltip
                    placement={"right"}
                    tooltip={maxScoreErrorMsg}
                    showArrow={true}
                  />
                }
                type="wrapperAsElement"
              >
                <TextInput
                  size={"large"}
                  value={isHL ? maxValueHL : maxValueSL}
                  style={styles.textInputStyles}
                  onChange={e => {
                    onChangeInput({
                      id,
                      isMinValue: false,
                      value: e.target.value,
                      isHL,
                      index,
                    });
                  }}
                  error={maxScoreHasError}
                  disabled={disableTextInput}
                  autoFocus={autoFocusKey === maxScoreKey}
                  onFocus={() => setAutoFocusKey(maxScoreKey)}
                  onBlur={() => setAutoFocusKey("")}
                />
              </ConditionalWrapper>
            ) : (
              <div className={classes.score}>
                {isHL ? maxValueHL : maxValueSL}
              </div>
            )
          ) : (
            <div className={classes.orHigherText}>
              {t("academicSetup:percentage_or_higher")}
            </div>
          )}
        </div>
      );
    }
  }
};

const getTableHeaders = params => {
  const {
    t,
    isSameScale = false,
    subjectLevelGenericTags = [],
    hasSLWeight,
    hasHLWeight,
    isEditable,
    tableParams,
  } = params;

  const {
    style,
    gradeScaleLabel = t("academicSetup:grade_scale"),
    sameScaleLabel = t("academicSetup:score_range_for_sl_and_hl"),
    scoreRangeLabel = t("assessmentEvaluation:score_range"),
    scoreRangeLabelSL = t("academicSetup:score_range_for_sl"),
    scoreRangeLabelHL = t("academicSetup:score_range_for_hl"),
    alignment: { gradeScaleAlignment, scoreRangeAlignment } = {},
  } = tableParams || {};

  const alignment = isEditable ? "center" : "left";
  const headerItems = [
    {
      key: "GRADE_SCALE",
      value: gradeScaleLabel,
      alignment: gradeScaleAlignment || alignment,
      style,
    },
  ];

  if (isSameScale) {
    headerItems.push({
      key: "SAME_SCALE",
      value: sameScaleLabel,
      alignment: scoreRangeAlignment || alignment,
      style,
    });
  } else {
    if (_.isEmpty(subjectLevelGenericTags)) {
      headerItems.push({
        key: "SCORE_RANGE",
        value: scoreRangeLabel,
        alignment: scoreRangeAlignment || alignment,
        style,
      });
    } else {
      if (hasSLWeight) {
        headerItems.push({
          key: "SCORE_RANGE_SL",
          value: scoreRangeLabelSL,
          alignment: scoreRangeAlignment || alignment,
          style,
        });
      }
      if (hasHLWeight) {
        headerItems.push({
          key: "SCORE_RANGE_HL",
          value: scoreRangeLabelHL,
          alignment: scoreRangeAlignment || alignment,
          style,
        });
      }
    }
  }

  return headerItems;
};

const getTableData = params => {
  const { headers, gradeBoundary = [] } = params || {};

  const data = _.map(gradeBoundary, (boundary, index) => {
    const isLastRow = index === _.size(gradeBoundary) - 1;
    const isFirstRow = index === 0;

    const rowData = _.map(headers, header => {
      return {
        key: _.get(header, "key"),
        value: getTableRowValue({
          header,
          boundary,
          isFirstRow,
          isLastRow,
          index,
          ...params,
        }),
      };
    });

    return {
      id: _.get(boundary, "grade"),
      rowData,
    };
  });

  return data;
};

const getIsSaveButtonDisabled = params => {
  const { gradeBoundary, hasSLWeight, hasHLWeight, isSameScale } = params || {};

  return _.reduce(
    gradeBoundary,
    (acc, value, index) => {
      const scoresSL = _.get(value, "SL", {});
      const scoresHL = _.get(value, "HL", {});

      const isLastRow = index === _.size(gradeBoundary) - 1;

      const { minValue: minScoreSL, maxValue: maxScoreSL } = scoresSL;
      const { minValue: minScoreHL, maxValue: maxScoreHL } = scoresHL;

      if (isSameScale) {
        const disable = isLastRow ? !_.toString(maxScoreSL) : false;
        const accSL = (acc = acc || !_.toString(minScoreSL) || disable);
        return accSL;
      }

      if (hasSLWeight && hasHLWeight) {
        const disable = isLastRow
          ? !(_.toString(maxScoreSL) && _.toString(maxScoreHL))
          : false;
        return (acc =
          acc ||
          !(_.toString(minScoreSL) && _.toString(minScoreHL)) ||
          disable);
      } else if (hasHLWeight) {
        const disable = isLastRow ? !_.toString(maxScoreHL) : false;
        return (acc = acc || !_.toString(minScoreHL) || disable);
      } else {
        const disable = isLastRow ? !_.toString(maxScoreSL) : false;
        const accSL = (acc = acc || !_.toString(minScoreSL) || disable);
        return accSL;
      }
    },
    false
  );
};

const TableWithLoader = withLoader(Table);

const getHasHLOrSLWeight = params => {
  const {
    categoryScoreValues = [],
    subjectLevelGenericTags = [],
    gradeBoundaryMethod,
    assessmentToolType,
  } = params || {};

  const isCategoryGradeBoundary =
    assessmentToolType === ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY ||
    gradeBoundaryMethod === "category_boundary";

  const scoreValueSL = _.find(categoryScoreValues, value => {
    return _.get(value, "genericTag.label") === "SL";
  })?.value;

  const scoreValueHL = _.find(categoryScoreValues, value => {
    return _.get(value, "genericTag.label") === "HL";
  })?.value;

  let hasSLWeight = false;
  let hasHLWeight = false;
  _.forEach(subjectLevelGenericTags, tag => {
    const tagLabel = _.get(tag, "label");
    if (_.isEqual(tagLabel, "SL")) {
      if (!isCategoryGradeBoundary || parseInt(scoreValueSL) > 0) {
        hasSLWeight = true;
      }
    } else if (_.isEqual(tagLabel, "HL")) {
      if (!isCategoryGradeBoundary || parseInt(scoreValueHL) > 0) {
        hasHLWeight = true;
      }
    }
  });

  // core / other subject group classes do not have HL or SL
  const noSLorHL = !(hasHLWeight || hasSLWeight);

  return { hasHLWeight, hasSLWeight, noSLorHL };
};

const AutomaticIBScaleTool = props => {
  const {
    t,
    subjectLevelGenericTags,
    subjectType,
    toggleAssessmentToolModal,
    mode = "create",
    categoryGradeBoundaries,
    termGradeBoundaries,
    categoryGradeBoundaryValues,
    isData,
    isLoading,
    assessmentTool = {},
    academicCriteriaValues,
    ibPYPElementRatings,
    isEditable,
    isToolCreated,
    hasTermGradeBoundary,
    hasCategoryGradeBoundary,
    updateCategoryGradeBoundary,
    setToastMsg,
    title,
    categoryId,
    showModalInViewModal = true,
    updateQueryParameters,
    location,
    assessmentToolType,
    onlyShowTable = false,
    tableParams = {},
    maxScore,
    studentCourseTags,
    studentDetails,
    categoryScoreValues,
    isSplitView = false,
    customRatingElementClass,
    customSelectedRatingElement,
    showAccordian = true,
    toolStyles,
    showTitle = true,
    viewTableContainerStyle = {},
    portalType,
    isGeneratingAIResponse,
  } = props;

  // states

  const [isSameScale, setIsSameScale] = useState(false);
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);
  const [gradeBoundary, setGradeBoundary] = useState(
    categoryGradeBoundaryValues
  );
  const [autoFocusKey, setAutoFocusKey] = useState("");
  const [gradeBoundaryMethod, setGradeBoundaryMethod] = useState("");
  const [errorsCount, setErrorsCount] = useState(0);
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(false);

  const [showPopover, setShowPopover] = useState(false);

  const infoIconRef = useRef(null);

  const { hasSLWeight, hasHLWeight, noSLorHL } = getHasHLOrSLWeight({
    categoryScoreValues,
    subjectLevelGenericTags,
    gradeBoundaryMethod,
    assessmentToolType,
  });

  // side effects

  const gradeBoundaryDependencyArray =
    mode === "preview"
      ? [subjectType, academicCriteriaValues, categoryGradeBoundaryValues]
      : [subjectType, academicCriteriaValues];

  useEffect(() => {
    if (!_.isEmpty(academicCriteriaValues)) {
      const updatedCategoryGradeBoundaries = getCategoryGradeBoundaryValues({
        categoryGradeBoundaries: getFilteredGradeBoundaries({
          gradeBoundary: {
            categories: academicCriteriaValues,
          },
          subjectLevelGenericTags,
          scoreKey: "scoreValues",
        }),
        categoryGradeBoundaryValues,
        subjectType,
      });

      setGradeBoundary(updatedCategoryGradeBoundaries);
    }
  }, gradeBoundaryDependencyArray);

  useEffect(() => {
    if (!_.includes(["view", "preview"], mode)) {
      if (isToolCreated) {
        let key = "";
        switch (assessmentToolType) {
          case ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY:
            key = "category_boundary";
            break;
          case ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY:
            key = "term_boundary";
            break;
          default:
            key = "custom_boundary";
        }

        setGradeBoundaryMethod(key);
      } else {
        if (hasCategoryGradeBoundary) {
          const newGradeBoundaryValues = getCategoryGradeBoundaryValues({
            categoryGradeBoundaries,
            categoryGradeBoundaryValues,
            subjectType,
          });

          setGradeBoundaryMethod("category_boundary");
          setGradeBoundary(newGradeBoundaryValues);
        } else if (hasTermGradeBoundary) {
          const newGradeBoundaryValues = getCategoryGradeBoundaryValues({
            categoryGradeBoundaries: termGradeBoundaries,
            categoryGradeBoundaryValues,
            subjectType,
          });

          setGradeBoundaryMethod("term_boundary");
          setGradeBoundary(newGradeBoundaryValues);
        } else {
          setGradeBoundaryMethod("custom_boundary");
          setGradeBoundary(categoryGradeBoundaryValues);
        }
      }
    }
  }, [hasCategoryGradeBoundary, hasTermGradeBoundary, subjectType]);

  useEffect(() => {
    // if both HL and SL boundaries are same then set is same scale to true
    if (
      assessmentToolType === ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY &&
      gradeBoundaryMethod === "custom_boundary" &&
      isToolCreated &&
      mode !== "view"
    ) {
      setIsSameScale(
        checkIsSameScale({
          categoryGradeBoundaryValues: gradeBoundary,
          subjectLevelGenericTags,
        })
      );
    }
  }, [isToolCreated, gradeBoundary, subjectLevelGenericTags]);

  const errors = checkAreScoresValid({
    t,
    categoryGradeBoundaryValues: gradeBoundary,
    hasSL: hasSLWeight,
    hasHL: hasHLWeight,
    noSLorHL,
    isSameScale,
  });

  useEffect(() => {
    if (mode !== "view") {
      setIsSaveBtnDisabled(
        getIsSaveButtonDisabled({
          gradeBoundary,
          hasHLWeight,
          hasSLWeight,
          isSameScale,
        })
      );

      if (!isSaveBtnDisabled) {
        setErrorsCount(_.size(errors));
      }
    }

    return () => setErrorsCount(0);
  }, [isSaveBtnDisabled, errors]);

  // functions

  const onChangeScale = () => {
    if (!isSameScale) {
      const gradeBoundaryValues = copySLGradeScaleToHL({
        categoryGradeBoundaryValues: gradeBoundary,
      });

      setGradeBoundary(gradeBoundaryValues);
    }
    setIsSameScale(!isSameScale);
  };

  const onChangeInput = params => {
    const { isHL, isMinValue, value, index } = params;

    // allow only numbers and upto 2 decimals
    if (!DOUBLE_DIGIT_DECIMAL_NUMBERS_REGEX.test(value) && value) return;

    const tagToUpdate = isHL ? "HL" : "SL";
    const valueToUpdate = isMinValue ? "minValue" : "maxValue";

    const updatedValues = _.cloneDeep(gradeBoundary);
    const maxValueKey = "maxValue";

    if (isSameScale) {
      _.set(_.get(updatedValues, [index]), ["HL", valueToUpdate], value);
      _.set(_.get(updatedValues, [index]), ["SL", valueToUpdate], value);
      if (index > 0 && valueToUpdate === "minValue") {
        _.set(_.get(updatedValues, [index - 1]), ["HL", maxValueKey], value);
        _.set(_.get(updatedValues, [index - 1]), ["SL", maxValueKey], value);
      }
    } else {
      _.set(_.get(updatedValues, [index]), [tagToUpdate, valueToUpdate], value);
      if (index > 0 && valueToUpdate === "minValue") {
        _.set(
          _.get(updatedValues, [index - 1]),
          [tagToUpdate, maxValueKey],
          value
        );
      }
    }

    setGradeBoundary(updatedValues);
    setIsSaveBtnDisabled(
      getIsSaveButtonDisabled({
        gradeBoundary,
        hasHLWeight,
        hasSLWeight,
        isSameScale,
      })
    );
  };

  const getTableProps = () => {
    const splitViewTableParams = isSplitView
      ? {
          style: {
            height: "42px",
            minWidth: "88px",
            maxWidth: "132px",
          },
          gradeScaleLabel: t("common:ib_grade"),
          scoreRangeLabelHL: t("common:score_label", {
            label: t("common:HL"),
          }),
          scoreRangeLabelSL: t("common:score_label", {
            label: t("common:SL"),
          }),
          alignment: {
            gradeScaleAlignment: "center",
            scoreRangeAlignment: "left",
          },
        }
      : tableParams;

    const headers = getTableHeaders({
      t,
      isSameScale,
      subjectLevelGenericTags,
      hasSLWeight,
      hasHLWeight,
      isEditable,
      tableParams: splitViewTableParams,
    });

    const data = getTableData({
      t,
      headers,
      subjectType,
      gradeBoundary,
      onChangeInput,
      isEditable,
      mode,
      errors,
      autoFocusKey,
      setAutoFocusKey,
      disableTextInput: gradeBoundaryMethod !== "custom_boundary",
      errorsCount,
      showModalInViewModal,
    });

    return { headers, data };
  };

  const onClickSave = async () => {
    if (_.size(errors) > 0) {
      setToastMsg({
        msg: "toastMsgs:rectify_errors_with_count",
        locale_params: [
          { key: "count", value: _.size(errors), isPlainText: true },
        ],
        type: "warning",
        position: "toast-top-center",
      });
      return;
    }

    const {
      createAssessmentTool,
      parentId,
      parentType,
      assessmentId,
      categoryPointSetId,
      termPointSetId,
      removeAssessmentTool,
    } = props;

    setShowFullScreenLoader(true);

    const shouldCreateNewTool = getShouldCreateNewTool({
      gradeBoundaryMethod,
      assessmentToolType,
    });

    if (_.isEmpty(assessmentTool) || shouldCreateNewTool) {
      if (shouldCreateNewTool) {
        // delete previous tool
        await removeAssessmentTool({
          id: _.get(assessmentTool, "id"),
          type: assessmentToolType,
          parentId,
          parentType,
          portalType,
        });
      }

      let criteriaSetId = "";
      let type = ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY;

      switch (gradeBoundaryMethod) {
        case "category_boundary":
          criteriaSetId = categoryPointSetId;
          type = ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY;
          break;
        case "term_boundary":
          criteriaSetId = termPointSetId;
          type = ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY;
          break;
      }

      const extraParams = {
        label: type,
      };

      if (criteriaSetId) {
        extraParams["criteriaSetId"] = criteriaSetId;
      } else {
        extraParams["associatedEntites"] = [
          {
            id: parentId,
            type: parentType,
          },
        ];

        const categories = getCategoriesForGradeBoundaryTool({
          subjectType,
          gradeBoundary,
          subjectLevelGenericTags,
          type,
        });

        if (_.isEmpty(categories)) {
          setToastMsg("toastMsgs:something_went_wrong");
          setShowFullScreenLoader(false);
          return;
        }

        extraParams["categories"] = categories;
      }

      await createAssessmentTool({
        type,
        parentId,
        parentType,
        assessmentId,
        extraParams,
        portalType,
      });
    } else {
      if (gradeBoundaryMethod === "custom_boundary") {
        const extraParams = {
          subType: "ASSESSMENT_CUSTOM_GRADE_BOUNDARY",
        };

        await updateCategoryGradeBoundary({
          categoryGradeBoundaryValues: gradeBoundary,
          subjectLevelGenericTags,
          gradeBoundaryId: _.get(assessmentTool, "id"),
          extraParams,
          portalType,
        });
      }
    }
    setShowFullScreenLoader(false);

    toggleAssessmentToolModal?.();
  };

  if (mode === "view" || mode === "preview") {
    const { gradeScaleLabel = {} } = toolStyles || {};
    const isExpanded = _.get(location, "query.isExpanded", false);

    const assessmentToolId = _.get(assessmentTool, "id");

    const selectedCriteria = _.find(ibPYPElementRatings, rating => {
      return (
        _.get(rating, "ibPYPElementId") === assessmentToolId &&
        !rating?.isDeleted
      );
    });

    const criteriaRating = (
      <Tooltip
        placement={"left"}
        tooltip={t("common:ib_grade_automatically_calculated")}
        showArrow={true}
      >
        <div>
          {isGeneratingAIResponse ? (
            <div className={classes.skeletonContainer}>
              {_.times(_.size(academicCriteriaValues), index => {
                return <SkeletonLoader key={index} width={44} height={32} />;
              })}
            </div>
          ) : (
            <CriteriaRating
              isEvaluation={false}
              options={_.orderBy(academicCriteriaValues, "label")}
              value={_.get(selectedCriteria, "academicCriteriaValue.id", "")}
              isViewMode={true}
              containerStyle={styles.criteriaRatingContainerStyle}
              customViewModeClass={classes.customViewModeClass}
              customRatingElementClass={
                customRatingElementClass
                  ? customRatingElementClass
                  : classes.customRatingElementClass
              }
              customSelectedRatingElement={
                customSelectedRatingElement
                  ? customSelectedRatingElement
                  : classes.customSelectedRatingElement
              }
              shouldUpdateOnValueChnage={true}
            />
          )}
        </div>
      </Tooltip>
    );

    const tableWithLoader = (
      <TableWithLoader
        isData={isData}
        isLoading={isLoading}
        showBorder={true}
        isHeaderFixed={isEditable}
        {...getTableProps()}
      />
    );

    const customTitleComponent = showTitle ? (
      <header className={classes.header}>
        <div className={classes.headerLeftContainer}>
          <div className={classes.ibGradeScale} style={gradeScaleLabel}>
            {t("common:ib_grade_scale")}
          </div>
          <div className={classes.automatedGrading}>
            {t("classRoom:automated_grading")}
            {" - "}
            {getAutoEvaluateIBGradeToolTypeName({ t, assessmentToolType })}
          </div>
        </div>
        <div className={classes.headerRightContainer}>
          {showModalInViewModal ? (
            <IconButton
              dsVersion="2.0"
              icon={<CloseOutlined variant="subtle" />}
              size={"medium"}
              variant="neutral"
              type="plain"
              onClick={toggleAssessmentToolModal}
            />
          ) : (
            criteriaRating
          )}
        </div>
      </header>
    ) : null;

    const modalContentClass = classNames(
      classes.modalContent,
      classes.viewModeModalContent
    );

    const tags = getStudentTags({
      studentCourseTags,
      studentId: _.get(studentDetails, "studentId"),
    });
    const genericTagId = _.get(tags, "0.id");

    const gradeBoundaryMaxValue = getGradeBoundaryMaxVal({
      academicCriteriaValues,
      genericTagId,
    });

    const expandedContent = (
      <div
        className={classes.viewTableContainer}
        style={viewTableContainerStyle}
      >
        <Table
          showBorder={true}
          isHeaderFixed={isEditable}
          {...getTableProps()}
        />

        {gradeBoundaryMaxValue != maxScore && mode === "view" ? (
          <div className={classes.pleaseNoteLabel}>
            {t("classRoom:auto_evaluate_ib_grade_tool_msg", {
              label: gradeBoundaryMaxValue,
            })}
          </div>
        ) : null}
      </div>
    );

    const popoverContainer = (
      <div className={classes.popoverContainer}>
        <div className={classes.splitViewAutomatedGrading}>
          {t("classRoom:automated_grading")}
          {" - "}
          {getAutoEvaluateIBGradeToolTypeName({
            t,
            assessmentToolType,
          })}
        </div>
        {tableWithLoader}
      </div>
    );

    if (onlyShowTable) {
      return popoverContainer;
    }

    if (isSplitView) {
      return (
        <React.Fragment>
          <div className={classes.splitViewContainer}>
            <div className={classes.splitViewLeftContainer}>
              {t("common:ib_grade")}
              <IconButton
                dsVersion="2.0"
                icon={<InformationOutlined variant="subtle" />}
                variant="neutral"
                type="plain"
                size="x-small"
                onClick={() => setShowPopover(prevState => !prevState)}
                ref={infoIconRef}
              />
            </div>
            {criteriaRating}
          </div>

          {showPopover && infoIconRef.current ? (
            <Popover
              isOpen={true}
              align={"start"}
              boundaryInset={50}
              positions={["left"]}
              containerStyle={styles.popoverContainerStyle}
              reposition={true}
              onClickOutside={() => setShowPopover(false)}
              content={({ position, childRect, popoverRect }) => (
                <ArrowContainer
                  position={position}
                  childRect={childRect}
                  popoverRect={popoverRect}
                  arrowColor={colors.white}
                  arrowSize={10}
                >
                  {popoverContainer}
                </ArrowContainer>
              )}
              childrenRef={{
                current: infoIconRef.current,
              }}
              isChildrenRefPassed={true}
            ></Popover>
          ) : null}
        </React.Fragment>
      );
    }

    if (!showAccordian) {
      return (
        <React.Fragment>
          {customTitleComponent}
          {expandedContent}
        </React.Fragment>
      );
    }

    return (
      <ConditionalWrapper
        wrapper={<UIModal isOpen modalContent={modalContentClass} />}
        condition={showModalInViewModal}
        type={"wrapperAsElement"}
      >
        {showModalInViewModal ? (
          <React.Fragment>
            <div className={classes.customTitleComp}>
              {customTitleComponent}
            </div>
            <Table
              showBorder={true}
              isHeaderFixed={isEditable}
              {...getTableProps()}
            />
          </React.Fragment>
        ) : (
          <Accordian
            customTitleComponent={customTitleComponent}
            expandedContent={expandedContent}
            hideContainerBorder={true}
            showLeftArrow={true}
            customLeftIcon={
              <IconButton
                dsVersion="2.0"
                icon={<ChevronRightOutlined variant="subtle" />}
                variant="neutral"
                type="plain"
                size={"x-small"}
              />
            }
            isLeftIconContainerFocusable={false}
            leftArrowStyle={styles.leftArrowStyle}
            headerStyle={styles.headerStyle}
            showLabelBorder={false}
            getIsExpanded={value => {
              updateQueryParameters({
                params: {
                  isExpanded: !value,
                },
                navigateType: "replace",
              });
            }}
            isExpanded={JSON.parse(isExpanded)}
          />
        )}
      </ConditionalWrapper>
    );
  }

  const hasBothScales = hasSLWeight && hasHLWeight;

  const onClickGradeBoundary = e => {
    const key = _.get(e, "key");

    if (key === gradeBoundaryMethod) return;

    switch (key) {
      case "category_boundary":
        {
          const newGradeBoundaryValues = getCategoryGradeBoundaryValues({
            categoryGradeBoundaries,
            categoryGradeBoundaryValues,
            subjectType,
          });
          setGradeBoundary(newGradeBoundaryValues);
          setIsSameScale(false);
        }
        break;
      case "term_boundary":
        {
          const newGradeBoundaryValues = getCategoryGradeBoundaryValues({
            categoryGradeBoundaries: termGradeBoundaries,
            categoryGradeBoundaryValues,
            subjectType,
          });
          setGradeBoundary(newGradeBoundaryValues);
          setIsSameScale(false);
        }
        break;
      default: {
        if (
          !isToolCreated ||
          assessmentToolType !== ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY
        ) {
          setGradeBoundary(categoryGradeBoundaryValues);
        } else {
          const newGradeBoundaryValues = getCategoryGradeBoundaryValues({
            categoryGradeBoundaries: {
              categories: _.get(assessmentTool, "academicCriteriaValues", []),
            },
            categoryGradeBoundaryValues,
            subjectType,
          });
          setGradeBoundary(newGradeBoundaryValues);
        }
      }
    }

    setGradeBoundaryMethod(key);
  };

  const gradeBoundaryOptions = getGradeBoundaryOptions({
    t,
    hasCategoryGradeBoundary,
    hasTermGradeBoundary,
    categoryId,
    assessmentToolType,
  });

  const buttonLabel = _.find(
    gradeBoundaryOptions,
    ({ key }) => key === gradeBoundaryMethod
  )?.label;

  return (
    <UIModal
      isOpen={true}
      modalContent={classes.modalContent}
      shouldCloseOnOverlayClick={false}
    >
      {isLoading ? (
        <Loading />
      ) : (
        <div className={classes.container}>
          <header className={classes.header}>
            <div className={classes.headerLeftContainer}>
              <ConditionalWrapper
                type={"wrapperAsElement"}
                condition={_.size(title) > 50}
                wrapper={
                  <Tooltip placement={"top"} tooltip={title} showArrow={true} />
                }
              >
                <div className={classes.headerTitle}>
                  {t("classRoom:define_grade_boundary_for_label", {
                    label: title,
                  })}
                </div>
              </ConditionalWrapper>
            </div>
            <div className={classes.headerRightContainer}>
              <IconButton
                dsVersion="2.0"
                icon={<CloseOutlined variant="subtle" />}
                size={"medium"}
                variant="neutral"
                type="plain"
                onClick={toggleAssessmentToolModal}
              />
            </div>
          </header>

          <div className={classes.contentSpaceBetween}>
            <div className={classes.subtext}>
              {t("classRoom:auto_evaluate_grade_boudary_tool_subtext")}
            </div>
            <Dropdown
              overlay={
                <DropdownMenu
                  options={gradeBoundaryOptions}
                  onClick={onClickGradeBoundary}
                  hasMultilineOptions={true}
                  itemTitleContainerStyle={styles.itemTitleContainerStyle}
                />
              }
            >
              <ButtonDisclosure
                dsVersion="2.0"
                size="medium"
                variant="neutral"
                type="outlined"
              >
                {buttonLabel}
              </ButtonDisclosure>
            </Dropdown>
          </div>

          {hasBothScales && gradeBoundaryMethod === "custom_boundary" ? (
            <div className={classes.flexWithGap} onClick={onChangeScale}>
              <Checkbox isChecked={isSameScale} />
              <div className={classes.sameScaleLabel}>
                {t("academicSetup:use_same_scale_for_sl_hl")}
              </div>
            </div>
          ) : null}

          <div className={classes.tableContainer}>
            <Table
              showBorder={true}
              isHeaderFixed={isEditable}
              {...getTableProps()}
            />
          </div>

          <footer className={classes.footer}>
            <Button
              dsVersion="2.0"
              size="medium"
              variant="neutral"
              type="outlined"
              onClick={toggleAssessmentToolModal}
            >
              {isToolCreated ? t("common:discard_changes") : t("common:cancel")}
            </Button>
            <Button
              dsVersion="2.0"
              size="medium"
              variant={"progressive"}
              onClick={onClickSave}
              disabled={isSaveBtnDisabled}
            >
              {isToolCreated ? t("common:save_changes") : t("common:save")}
            </Button>
          </footer>
        </div>
      )}

      {showFullScreenLoader ? <FullScreenLoader /> : null}
    </UIModal>
  );
};

const getDerivedProps = props => {
  const {
    categoryGradeBoundaryValues,
    assessmentFields,
    subjectType: propsSubjectType,
    subjectId,
    mode,
  } = props;

  // when course context is not present then subjectType and categoryGradeBoundaryValues will be empty (due to query skip)
  // calculate the necessary values from assessmentFields (for custom grade boundary to work)
  if (
    _.isEmpty(propsSubjectType) &&
    _.isEmpty(categoryGradeBoundaryValues) &&
    mode !== "view"
  ) {
    const subjects = getResolvedMinimalTreeSet({
      fields: assessmentFields,
      uid: "subjects",
    });

    const subjectType = _.get(subjects, "0.type", "");
    const subjectLabel = _.get(subjects, "0.name");

    const categoryGradeBoundaryValues = getGradeBoundaryInitialValues({
      subjectType,
    });

    const { subjectLevelGenericTags } = getSubjectNameAndGenericTags({
      subjects,
      subjectId,
    });

    return {
      subjectType,
      subjectLabel,
      subjectLevelGenericTags,
      categoryGradeBoundaryValues,
    };
  }

  return {};
};

const mapStateToProps = (state, ownProps) => {
  const {
    t,
    assessmentFields = [],
    category = {},
    academicTermId,
    assessmentTool,
    mode,
    assessmentTools = [],
    portalType,
  } = ownProps;

  const title =
    _.find(assessmentFields, field => {
      return field?.uid === "title";
    })?.value || t("common:untitled");

  const organizationId = state.login.userInfo.org_id;

  const _assessmentTools = assessmentTools?.edges
    ? assessmentTools?.edges
    : assessmentTools;

  const { node: { assessmentToolItem, assessmentToolType } = {} } =
    _.find(_assessmentTools, ({ node }) => {
      return getIsIBGradeScaleTool({
        assessmentToolType: node?.assessmentToolType,
        checkForManual: false,
      });
    }) || {};

  const isToolCreated = mode === "edit";
  const isEditable = mode === "create" || isToolCreated;

  const { academicCriteriaValues = [] } =
    assessmentTool || assessmentToolItem || {};

  const { id: categoryId, label: categoryLabel } = category || {};

  const curriculumProgramId = _.get(
    state,
    "platform.currentCurriculumProgram.id"
  );

  const subjects = _.find(assessmentFields, field => {
    return _.get(field, "uid") === "subjects";
  });

  const subjectId = !_.isEmpty(subjects)
    ? _.first(subjects?.value)
    : _.get(ownProps, "subjects.0.id") || _.get(ownProps, "subjectId");

  let courseId = _.get(state, "teacher.selected_class.selected_course", "");

  courseId = !_.isEmpty(courseId) ? courseId : ownProps?.selectedCourseId;

  const categoryPointSetVariables = {
    id: organizationId,
    filters: {
      types: "GRADE_BOUNDARY",
      curriculumProgramIds: curriculumProgramId,
      associatedEntites: [{ ids: categoryId, type: "ACADEMIC_CRITERIA_VALUE" }],
    },
    portalType,
  };

  const { maxScore } = getScoreDetailsMemoize({
    assessmentTools,
  });

  const academicYearId = getSelectedAcademicYearId(state);

  return {
    isData: true,
    isLoading: false,
    subjectId,
    categoryPointSetVariables,
    categoryId,
    academicTermId,
    courseId,
    academicCriteriaValues,
    isEditable,
    isToolCreated,
    categoryLabel,
    assessmentTool: assessmentTool || assessmentToolItem,
    title,
    maxScore,
    assessmentToolType,
    organizationId,
    curriculumProgramId,
    academicYearId,
    learningCourseId:
      _.get(state, "planner.unitPlanData.learningCourseId", null) ||
      getSelectedLearningCourseId(state) ||
      _.get(ownProps, "learningCourseId"),
    isLearningCoursesEnabled: getIsLearningCoursesEnabled(),
  };
};

const mapActionCreators = {
  createAssessmentTool,
  updateCategoryGradeBoundary,
  setToastMsg,
  removeAssessmentTool,
  updateQueryParameters,
};

export default compose(
  I18nHOC({ resource: ["common", "academicSetup"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getCategoryScoreValuesQuery, {
    name: "getCategoryScoreValues",
    skip: ({ categoryId }) => !categoryId,
    options: ({ categoryId, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: categoryId,
        portalType,
      },
      context: {
        useHttpLink: true,
      },
    }),
    props({
      getCategoryScoreValues,
      ownProps: { categoryId, isData, isLoading },
    }) {
      const queryData = getCategoryScoreValuesFromCache({
        id: categoryId,
      });

      const categoryScoreValues = _.get(queryData, "scoreValues", []);

      return {
        categoryScoreValues,
        isData: !_.isEmpty(queryData) && isData,
        isLoading:
          _.includes([1, 2], getCategoryScoreValues.networkStatus) || isLoading,
      };
    },
  }),
  graphql(getCourseSubjectFlatVariantsLevelQuery, {
    name: "getCourseSubjectFlatVariantsLevel",
    skip: ({ courseId, isLearningCoursesEnabled }) =>
      !courseId || isLearningCoursesEnabled,
    options: ({ courseId, portalType, academicYearId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: courseId,
        gradeFilters: { academicYearId },
        portalType,
      },
    }),
    props({
      getCourseSubjectFlatVariantsLevel,
      ownProps: {
        isData,
        isLoading,
        organizationId,
        curriculumProgramId,
        subjectId,
        academicTermId,
        portalType,
      },
    }) {
      const courseData = getSubjectFlatVariantsLevelFromCache(
        getCourseSubjectFlatVariantsLevel.variables
      );

      const subjects = _.get(courseData, "subjects", []);
      const gradeId = _.get(courseData, "grade.id");

      const subjectType = _.get(subjects, "0.type");
      const subjectLabel = _.get(subjects, "0.name");

      const { subjectLevelGenericTags } = getSubjectNameAndGenericTags({
        subjects,
        subjectId,
      });

      const networkStatus = _.get(
        getCourseSubjectFlatVariantsLevel,
        "networkStatus"
      );

      const categoryGradeBoundaryValues = getGradeBoundaryInitialValues({
        subjectType,
      });

      const termPointSetVariables = {
        id: organizationId,
        filters: {
          types: "GRADE_BOUNDARY",
          curriculumProgramIds: curriculumProgramId,
          associatedEntites: [
            {
              type: "SUBJECT",
              ids: subjectId,
            },
            {
              type: "ACADEMIC_TERM",
              ids: academicTermId,
            },
            {
              type: "GRADE",
              ids: gradeId,
            },
          ],
        },
        portalType,
      };

      return {
        isData: !_.isEmpty(courseData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        subjectLevelGenericTags,
        subjectType,
        categoryGradeBoundaryValues,
        gradeId,
        subjectLabel,
        termPointSetVariables,
      };
    },
  }),
  graphql(getLearningCourseSubjectFlatVariantsLevelQuery, {
    name: "getLearningCourseSubjectFlatVariantsLevel",
    skip: ({ learningCourseId, isLearningCoursesEnabled }) =>
      !learningCourseId || !isLearningCoursesEnabled,
    options: ({ learningCourseId, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: learningCourseId,
        portalType,
      },
    }),
    props({
      getLearningCourseSubjectFlatVariantsLevel,
      ownProps: {
        isData,
        isLoading,
        organizationId,
        curriculumProgramId,
        subjectId,
        academicTermId,
        portalType,
      },
    }) {
      const courseData = _.get(
        getLearningCourseSubjectFlatVariantsLevel,
        "node",
        {}
      );

      const subjects = _.get(courseData, "subjects", []);
      const gradeId = _.get(courseData, "grades.0.id");

      const subjectType = _.get(subjects, "0.type");
      const subjectLabel = _.get(subjects, "0.name");

      const { subjectLevelGenericTags } = getSubjectNameAndGenericTags({
        subjects,
        subjectId,
      });

      const networkStatus = _.get(
        getLearningCourseSubjectFlatVariantsLevel,
        "networkStatus"
      );

      const categoryGradeBoundaryValues = getGradeBoundaryInitialValues({
        subjectType,
      });

      const termPointSetVariables = {
        id: organizationId,
        filters: {
          types: "GRADE_BOUNDARY",
          curriculumProgramIds: curriculumProgramId,
          associatedEntites: [
            {
              type: "SUBJECT",
              ids: subjectId,
            },
            {
              type: "ACADEMIC_TERM",
              ids: academicTermId,
            },
            {
              type: "GRADE",
              ids: gradeId,
            },
          ],
        },
        portalType,
      };

      return {
        isData: !_.isEmpty(courseData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        subjectLevelGenericTags,
        subjectType,
        categoryGradeBoundaryValues,
        gradeId,
        subjectLabel,
        termPointSetVariables,
      };
    },
  }),
  // this is for category grade boundary
  graphql(getOrganizationPointSetQuery, {
    name: "getOrganizationPointSet",
    skip: ({ categoryId, mode }) => mode === "view" || !categoryId,
    options: ({ categoryPointSetVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: categoryPointSetVariables,
    }),
    props({
      getOrganizationPointSet,
      ownProps: { categoryPointSetVariables, isData, isLoading },
    }) {
      const organizationPointSetData = getOrganizationPointSetFromCache({
        variables: categoryPointSetVariables,
      });

      const organizationPointSet = _.get(
        organizationPointSetData,
        "pointAssessmentSets.edges",
        []
      );

      const categoryGradeBoundaries = _.get(
        _.first(organizationPointSet),
        "node",
        {}
      );

      const categoryPointSetId = _.get(categoryGradeBoundaries, "id");

      const hasCategoryGradeBoundary = !_.isEmpty(categoryGradeBoundaries);

      return {
        categoryGradeBoundaries,
        hasCategoryGradeBoundary,
        categoryPointSetId,
        organizationPointSetCount: _.get(
          organizationPointSetData,
          "pointAssessmentSets.totalCount",
          0
        ),
        isData: !_.isEmpty(organizationPointSetData) && isData,
        isLoading:
          _.includes([1, 2], getOrganizationPointSet.networkStatus) ||
          isLoading,
      };
    },
  }),
  // this is for term grade boundary
  graphql(getOrganizationPointSetQuery, {
    name: "getOrganizationPointSet",
    skip: ({ subjectId, academicTermId, gradeId, mode }) =>
      mode === "view" || !subjectId || !academicTermId || !gradeId,
    options: ({ termPointSetVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: termPointSetVariables,
    }),
    props({
      getOrganizationPointSet,
      ownProps: {
        termPointSetVariables,
        subjectLevelGenericTags,
        isData,
        isLoading,
      },
    }) {
      const termPointSetData = getOrganizationPointSetFromCache({
        variables: termPointSetVariables,
      });

      const termPointSetEdges = _.get(
        termPointSetData,
        "pointAssessmentSets.edges",
        []
      );

      const termGradeBoundaries = _.get(termPointSetEdges, "0.node", {});

      const termPointSetId = _.get(termGradeBoundaries, "id");

      const hasTermGradeBoundary = !_.isEmpty(termGradeBoundaries);

      return {
        termGradeBoundaries: getFilteredGradeBoundaries({
          gradeBoundary: termGradeBoundaries,
          subjectLevelGenericTags,
        }),
        hasTermGradeBoundary,
        termPointSetId,
        isData: !_.isEmpty(termPointSetData) && isData,
        isLoading:
          _.includes([1, 2], getOrganizationPointSet?.networkStatus) ||
          isLoading,
      };
    },
  }),
  withRouter,
  withDerivedProps(getDerivedProps)
)(AutomaticIBScaleTool);
